import { CSSResultGroup, TemplateResult, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import componentStyles from './sic-action-block.css.lit';

import { SlotsNGController as SlotController } from '../../controllers/slots-ng-controller';

import '../sic-container/sic-container';
import '../sic-container/sic-parallax-container/sic-parallax-container';
/**
 * The action block component
 *
 * @element sic-action-block
 * @slot image - The image for the action block
 * @slot heading - Heading for the action block
 * @slot text - Text for the action block
 * @slot cta-menu - CTA menu for the action block
 *
 * Lightdom styles in sic-action-block.lightdom.css included in /shared.css
 */
@customElement('sic-action-block')
export class SicActionBlock extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  slots = new SlotController(this);

  /**
   * For use with the parallax-container.
   * If a simple static image is preferred just pass your image into
   * the image slot.
   */
  @property({ type: String, attribute: 'parallax-bg-sm' })
  parallaxBgSm: string;

  @property({ type: String, attribute: 'parallax-bg-md' })
  parallaxBgMd: string;

  @property({ type: String, attribute: 'parallax-bg-lg' })
  parallaxBgLg: string;

  @property({ type: String, attribute: 'parallax-bg-ultra' })
  parallaxBgUltra: string;

  render(): TemplateResult {
    return html`
      ${this.slots.slotExists('image') || this.parallaxBgSm
        ? this.imageTemplate()
        : this.noImageTemplate()}
    `;
  }

  imageTemplate(): TemplateResult | null {
    return html`
      <div class="action-block-wrapper">
        ${this.slots.conditionalSlot('image', false)}
        <sic-parallax-container
          parallax-sm="${this.parallaxBgSm}"
          parallax-md="${this.parallaxBgMd}"
          parallax-lg="${this.parallaxBgLg}"
          parallax-ultra="${this.parallaxBgUltra}"
        >
          <div class="content">
            <div class="text-container-wrapper">
              <div class="text-content-wrapper">
                <div class="text-wrapper">
                  ${this.slots.conditionalSlot('heading')}
                  ${this.slots.conditionalSlot('text', false)}
                </div>
                ${this.slots.conditionalSlot('cta-menu', false)}
              </div>
            </div>
          </div>
        </sic-parallax-container>
      </div>
    `;
  }

  noImageTemplate(): TemplateResult | null {
    return html`
      <div class="action-block-wrapper">
        <sic-container top-padding="none" bottom-padding="none">
          <div class="content">
            <div class="text-container-wrapper">
              <div class="text-content-wrapper">
                <div class="text-wrapper">
                  ${this.slots.conditionalSlot('heading')}
                  ${this.slots.conditionalSlot('text', false)}
                </div>
                ${this.slots.conditionalSlot('cta-menu', false)}
              </div>
            </div>
          </div>
        </sic-container>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sic-action-block': SicActionBlock;
  }
}
